import type { PortfolioDetail } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { PORTFOLIO_PATH } from '@/shared/config';
import { Button, ImageWithFallback } from '@/shared/ui';

interface PortfolioDetailHeaderProps {
  readonly detail: PortfolioDetail;
  readonly onRetry: () => void;
}

/**
 * 사례 상세 머리 — 목록으로 돌아가는 작은 링크, 굵은 큰 제목, 오른쪽 고객사 로고,
 * 얇은 선 아래 고객사·분야·제작 상품 메타 줄. 고객사명·로고는 입력한 경우에만 표시한다.
 */
export const PortfolioDetailHeader = ({
  detail,
  onRetry,
}: PortfolioDetailHeaderProps): ReactElement => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [firstProduct, ...restProducts] = detail.products;
  return (
    <header className="wrap-anywhere">
      <Link
        to={PORTFOLIO_PATH}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span aria-hidden>←</span> Portfolio
      </Link>

      <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <h1 className="max-w-4xl text-[clamp(2rem,4.5vw,3.75rem)] leading-[1.1] font-bold tracking-[-0.03em] text-balance">
          {detail.title}
        </h1>
        {detail.brandLogoUrl !== null && (
          <section aria-label="고객사 로고" className="flex shrink-0 flex-col items-start gap-3">
            <div className="h-16 w-48">
              <ImageWithFallback
                key={`${detail.brandLogoUrl}:${attempt}`}
                src={detail.brandLogoUrl}
                alt={`${detail.clientName ?? detail.title} 로고`}
                loading="eager"
                className="size-full object-contain object-left lg:object-right"
                failureText="로고를 불러오지 못했습니다."
                onError={() => setFailedUrl(detail.brandLogoUrl)}
              />
            </div>
            {failedUrl === detail.brandLogoUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFailedUrl(null);
                  setAttempt((value) => value + 1);
                  onRetry();
                }}
              >
                로고 다시 불러오기
              </Button>
            )}
          </section>
        )}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-6 border-t border-foreground/10 pt-6 sm:grid-cols-3">
        {detail.clientName !== null && (
          <section aria-label="고객사 이름" className="flex flex-col gap-1.5">
            <p aria-hidden className="text-xs tracking-wide text-muted-foreground uppercase">
              Client
            </p>
            <p className="text-base font-semibold">{detail.clientName}</p>
          </section>
        )}
        <div className="flex flex-col gap-1.5">
          <p className="text-xs tracking-wide text-muted-foreground uppercase">Category</p>
          <p className="text-base font-semibold">
            {detail.categories.map((category) => category.name).join(' · ')}
          </p>
        </div>
        {firstProduct !== undefined && (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs tracking-wide text-muted-foreground uppercase">Products</p>
            <p className="text-base font-semibold">
              {firstProduct.name}
              {restProducts.length > 0 && ` 외 ${restProducts.length}종`}
            </p>
          </div>
        )}
      </div>
    </header>
  );
};
